import React, { useState, useEffect, useRef } from 'react';
import api from '../api';
import { FileText, Plus, Search, Trash2, ArrowLeft, Truck, Calendar } from 'lucide-react';
import { useAutosave, loadDraft, clearDraft } from '../hooks/useAutosave';

const fmtCurrency = (v) => `$${Number(v || 0).toFixed(2)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';

const PQ_KEY = 'webb_pq_draft';

const PurchaseQuotes = () => {
  const [quotes, setQuotes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const pqSaved = loadDraft(PQ_KEY);
  const [view, setView] = useState(pqSaved?.view || 'list');

  // Form state
  const [selectedVendor, setSelectedVendor] = useState(pqSaved?.selectedVendor || '');
  const [showAddVendor, setShowAddVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [validDays, setValidDays] = useState(pqSaved?.validDays || 30);
  const [notes, setNotes] = useState(pqSaved?.notes || '');
  const [lineItems, setLineItems] = useState(pqSaved?.lineItems || []);
  const [quoteDate, setQuoteDate] = useState(pqSaved?.quoteDate || new Date().toISOString().split('T')[0]);
  const [saving, setSaving] = useState(false);

  // Product search
  const [productSearch, setProductSearch] = useState('');
  const [showProductDD, setShowProductDD] = useState(false);
  const searchRef = useRef(null);
  const ddRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [q, v, p] = await Promise.all([
          api.request('/api/purchase-quotes'),
          api.getVendors(),
          api.getProducts(),
        ]);
        setQuotes(Array.isArray(q) ? q : []);
        setVendors(Array.isArray(v) ? v : []);
        setProducts(Array.isArray(p) ? p : []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  // Autosave purchase quote form
  useAutosave(PQ_KEY, { view, selectedVendor, validDays, notes, lineItems, quoteDate });

  useEffect(() => {
    const handler = (e) => {
      if (ddRef.current && !ddRef.current.contains(e.target) &&
          searchRef.current && !searchRef.current.contains(e.target)) {
        setShowProductDD(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredProducts = products.filter(p => {
    const q = productSearch.toLowerCase();
    return q.length >= 1 && (
      (p.name || '').toLowerCase().includes(q) ||
      (p.sku || '').toLowerCase().includes(q)
    );
  }).slice(0, 15);

  const addProduct = (p) => {
    if (lineItems.find(l => l.product_id === p.id)) return;
    setLineItems(prev => [...prev, {
      product_id: p.id, product_name: p.name, sku: p.sku || '',
      quantity: 1, unit_price: p.cost_price || p.unit_price || 0,
      total: p.cost_price || p.unit_price || 0,
    }]);
    setProductSearch('');
    setShowProductDD(false);
  };

  const addNewProduct = () => {
    const name = prompt('Enter new product name:');
    if (!name) return;
    const price = parseFloat(prompt('Enter unit price:', '0') || '0');
    setLineItems(prev => [...prev, {
      product_id: '', product_name: name, sku: '',
      quantity: 1, unit_price: price, total: price, is_new: true,
    }]);
  };

  const updateLine = (idx, field, value) => {
    setLineItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        updated.total = (parseFloat(updated.quantity) || 0) * (parseFloat(updated.unit_price) || 0);
      }
      return updated;
    }));
  };

  const removeLine = (idx) => setLineItems(prev => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedVendor && !showAddVendor) { alert('Select a vendor'); return; }
    if (lineItems.length === 0) { alert('Add at least one product'); return; }

    setSaving(true);
    try {
      let vendorId = selectedVendor;
      let vendorName = '';
      if (showAddVendor && newVendorName.trim()) {
        const nv = await api.createVendor({ name: newVendorName.trim() });
        vendorId = nv.id;
        vendorName = nv.name;
        const updated = await api.getVendors();
        setVendors(Array.isArray(updated) ? updated : []);
      } else {
        const v = vendors.find(v => v.id === vendorId);
        vendorName = v ? v.name : '';
      }

      await api.request('/api/purchase-quotes', {
        method: 'POST',
        body: JSON.stringify({
          vendor_id: vendorId, vendor_name: vendorName,
          line_items: lineItems, valid_days: validDays, notes,
          quote_date: quoteDate,
        }),
      });

      const q = await api.request('/api/purchase-quotes');
      setQuotes(Array.isArray(q) ? q : []);
      resetForm();
      setView('list');
    } catch (err) { alert('Failed: ' + err.message); }
    finally { setSaving(false); }
  };

  const resetForm = () => {
    setSelectedVendor(''); setShowAddVendor(false); setNewVendorName('');
    setValidDays(30); setNotes(''); setLineItems([]);
    setQuoteDate(new Date().toISOString().split('T')[0]);
    clearDraft(PQ_KEY);
  };

  const updateStatus = async (id, status) => {
    try {
      await api.request(`/api/purchase-quotes/${id}`, { method: 'PUT', body: JSON.stringify({ status }) });
      const q = await api.request('/api/purchase-quotes');
      setQuotes(Array.isArray(q) ? q : []);
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const convertToPO = async (id) => {
    if (!window.confirm('Convert this quote to a Purchase Order?')) return;
    try {
      const res = await api.request(`/api/purchase-quotes/${id}/convert-to-po`, { method: 'POST' });
      alert(`Purchase Order ${res.po_number} created!`);
      const q = await api.request('/api/purchase-quotes');
      setQuotes(Array.isArray(q) ? q : []);
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const deleteQuote = async (id) => {
    if (!window.confirm('Delete this quote?')) return;
    try {
      await api.request(`/api/purchase-quotes/${id}`, { method: 'DELETE' });
      setQuotes(prev => prev.filter(q => q.id !== id));
    } catch (err) { alert('Failed: ' + err.message); }
  };

  const statusBadge = (s) => {
    const map = {
      draft: 'bg-gray-100 text-gray-700', sent: 'bg-blue-100 text-blue-700',
      accepted: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
      expired: 'bg-amber-100 text-amber-700',
    };
    return <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[s] || 'bg-gray-100'}`}>{s}</span>;
  };

  const quoteTotal = lineItems.reduce((s, l) => s + (l.total || 0), 0);

  // CREATE VIEW
  if (view === 'create') return (
    <div className="space-y-6" data-testid="pq-create">
      <div className="flex items-center gap-3">
        <button onClick={() => { setView('list'); resetForm(); }} className="p-2 hover:bg-muted rounded"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="text-2xl font-bold">New Purchase Quote</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
        <div className="bg-card border rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold">Quote Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Vendor */}
            <div>
              <label className="block text-sm font-medium mb-1"><Truck className="inline h-4 w-4 mr-1" />Vendor</label>
              {!showAddVendor ? (
                <div className="space-y-1">
                  <select value={selectedVendor} onChange={e => { if (e.target.value === '__new__') setShowAddVendor(true); else setSelectedVendor(e.target.value); }}
                    className="w-full px-3 py-2 border border-input rounded-lg text-sm bg-background" data-testid="pq-vendor-select">
                    <option value="">Select vendor...</option>
                    {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    <option value="__new__">+ Add New Vendor</option>
                  </select>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input type="text" placeholder="New vendor name" value={newVendorName} onChange={e => setNewVendorName(e.target.value)}
                    className="flex-1 px-3 py-2 border border-input rounded-lg text-sm" data-testid="pq-new-vendor" />
                  <button type="button" onClick={() => setShowAddVendor(false)} className="px-2 py-1 text-xs border rounded hover:bg-muted">Back</button>
                </div>
              )}
            </div>
            {/* Valid For */}
            <div>
              <label className="block text-sm font-medium mb-1"><Calendar className="inline h-4 w-4 mr-1" />Quote Valid For</label>
              <div className="flex items-center gap-2">
                <input type="number" min="1" value={validDays} onChange={e => setValidDays(e.target.value)}
                  className="w-20 px-3 py-2 border border-input rounded-lg text-sm" data-testid="pq-valid-days" />
                <span className="text-sm text-muted-foreground">days</span>
              </div>
            </div>
            {/* Quote Date */}
            <div>
              <label className="block text-sm font-medium mb-1"><Calendar className="inline h-4 w-4 mr-1" />Quote Date</label>
              <input type="date" value={quoteDate} onChange={e => setQuoteDate(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg text-sm" data-testid="pq-quote-date" />
            </div>
            {/* Notes */}
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Optional notes..."
                className="w-full px-3 py-2 border border-input rounded-lg text-sm" data-testid="pq-notes" />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-card border rounded-lg p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Line Items</h3>
            <button type="button" onClick={addNewProduct} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="pq-add-new-product">
              <Plus className="h-3 w-3" /> Add New Product
            </button>
          </div>

          {/* Product search */}
          <div className="relative max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input ref={searchRef} type="text" placeholder="Search existing products..." value={productSearch}
              onChange={e => { setProductSearch(e.target.value); setShowProductDD(true); }}
              onFocus={() => { if (productSearch) setShowProductDD(true); }}
              className="w-full pl-9 pr-3 py-2 border border-input rounded-lg text-sm" data-testid="pq-product-search" />
            {showProductDD && filteredProducts.length > 0 && (
              <div ref={ddRef} className="absolute z-50 top-full mt-1 w-full bg-card border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {filteredProducts.map(p => (
                  <button key={p.id} type="button" onClick={() => addProduct(p)}
                    className="w-full text-left px-4 py-2 hover:bg-muted/50 border-b border-border/30 text-sm">
                    <span className="font-medium">{p.name}</span>
                    {p.sku && <span className="text-muted-foreground ml-2 font-mono text-xs">{p.sku}</span>}
                    {p.cost_price > 0 && <span className="text-muted-foreground ml-2">${p.cost_price?.toFixed(2)}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {lineItems.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Product</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground w-24">Qty</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground w-32">Unit Price</th>
                  <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground w-28">Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {lineItems.map((item, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2">
                      <span className="font-medium">{item.product_name}</span>
                      {item.sku && <span className="text-muted-foreground ml-2 font-mono text-xs">{item.sku}</span>}
                      {item.is_new && <span className="ml-2 px-1 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">New</span>}
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" min="1" value={item.quantity}
                        onChange={e => updateLine(idx, 'quantity', e.target.value)}
                        className="w-full px-2 py-1 border border-input rounded text-sm text-right" />
                    </td>
                    <td className="px-3 py-2">
                      <input type="number" step="0.01" min="0" value={item.unit_price}
                        onChange={e => updateLine(idx, 'unit_price', e.target.value)}
                        className="w-full px-2 py-1 border border-input rounded text-sm text-right" />
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtCurrency(item.total)}</td>
                    <td className="px-3 py-2">
                      <button type="button" onClick={() => removeLine(idx)} className="p-1 hover:text-red-600">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2">
                  <td colSpan="3" className="px-3 py-2 text-right font-semibold">Total:</td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold">{fmtCurrency(quoteTotal)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <button type="submit" disabled={saving}
          className="px-6 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
          data-testid="pq-submit-btn">
          {saving ? 'Creating...' : 'Create Purchase Quote'}
        </button>
      </form>
    </div>
  );

  // LIST VIEW
  return (
    <div className="space-y-6" data-testid="purchase-quotes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><FileText className="h-6 w-6" /> Purchase Quotes</h1>
          <p className="text-sm text-muted-foreground mt-1">Request pricing from vendors</p>
        </div>
        <button onClick={() => setView('create')}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 text-sm font-medium"
          data-testid="new-quote-btn">
          <Plus className="h-4 w-4" /> New Quote
        </button>
      </div>

      <div className="bg-card border rounded-lg overflow-hidden">
        <table className="w-full" data-testid="quotes-list-table">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Quote #</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Vendor</th>
              <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Total</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Expires</th>
              <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Created</th>
              <th className="text-center px-4 py-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">Loading...</td></tr>
            ) : quotes.length === 0 ? (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-muted-foreground">
                <FileText className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
                No purchase quotes yet
              </td></tr>
            ) : quotes.map(q => (
              <tr key={q.id} className="hover:bg-muted/20">
                <td className="px-4 py-3 font-medium text-sm">{q.quote_number}</td>
                <td className="px-4 py-3 text-sm">{q.vendor_name || '—'}</td>
                <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold">{fmtCurrency(q.total)}</td>
                <td className="px-4 py-3 text-center">{statusBadge(q.status)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {fmtDate(q.expiry_date)}
                  {q.expiry_date && new Date(q.expiry_date) < new Date() && q.status === 'draft' && (
                    <span className="ml-1 text-red-500 text-xs">Expired</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{fmtDate(q.created_at)}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-1">
                    {q.status === 'draft' && (
                      <>
                        <button onClick={() => updateStatus(q.id, 'sent')} className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200">Send</button>
                        <button onClick={() => convertToPO(q.id)} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Convert to PO</button>
                      </>
                    )}
                    {q.status === 'sent' && (
                      <>
                        <button onClick={() => convertToPO(q.id)} className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200">Accept</button>
                        <button onClick={() => updateStatus(q.id, 'rejected')} className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200">Reject</button>
                      </>
                    )}
                    <button onClick={() => deleteQuote(q.id)} className="p-1 text-muted-foreground hover:text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PurchaseQuotes;
